import { FC, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import toast from 'react-hot-toast';
import { Camera, useSettings } from '../contexts/settings-context';
import { CameraViewer } from './camera-viewer';
import { InputField } from './input-field';
import { BBox } from '../utils/card-crop';
import { calibrationFromBox } from '../utils/size-measure';

interface CameraCalibrationDialogProps {
  /** The camera to calibrate (id + url from the settings form row). */
  camera: Camera;
  open: boolean;
  onClose: () => void;
}

/**
 * Size calibration for a fixed camera: place an object of known size under it, adjust the box to
 * the object's edges (auto-seeded from detection), enter its real width/height, and Calibrate. That
 * fixes the scale so every future capture's box converts straight to real cm.
 */
export const CameraCalibrationDialog: FC<CameraCalibrationDialogProps> = ({
  camera,
  open,
  onClose,
}) => {
  const { settings, saveSettings } = useSettings();
  const existing = settings.cameraCalibration?.[camera.id];

  const [box, setBox] = useState<BBox | null>(null);
  const [frame, setFrame] = useState<{ w: number; h: number } | null>(null);
  const [widthCm, setWidthCm] = useState(String(existing?.refWidthCm ?? 7.5));
  const [heightCm, setHeightCm] = useState(String(existing?.refHeightCm ?? 10));

  // Re-seed the adjustable box each time the dialog opens (CameraViewer seeds it from a detection).
  // Keyed on the open transition, so an unrelated settings change never resets in-progress edits.
  useEffect(() => {
    if (open) {
      setBox(null);
      setWidthCm(String(existing?.refWidthCm ?? 7.5));
      setHeightCm(String(existing?.refHeightCm ?? 10));
    }
  }, [open, existing?.refWidthCm, existing?.refHeightCm]);

  const handleCalibrate = () => {
    const w = Number(widthCm);
    const h = Number(heightCm);
    if (!box || !frame) {
      toast.error('Waiting for the camera. Place the object under it and try again.');
      return;
    }
    if (!(w > 0) || !(h > 0)) {
      toast.error('Enter the object width and height in cm.');
      return;
    }
    const calibration = calibrationFromBox(
      { w: box.w, h: box.h, frameW: frame.w, frameH: frame.h },
      w,
      h,
    );
    if (!calibration) {
      toast.error('Could not read the box. Adjust it over the object and try again.');
      return;
    }
    saveSettings({
      ...settings,
      cameraCalibration: { ...settings.cameraCalibration, [camera.id]: calibration },
    });
    toast.success('Camera calibrated');
    onClose();
  };

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="md">
      <DialogTitle>Size calibration — {camera.title || 'camera'}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Place an object of known size under the camera, drag the box corners to its exact edges,
          then enter its real size and Calibrate. Future captures measure automatically.
        </DialogContentText>
        <Box sx={{ height: 420, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <CameraViewer
            camera={camera}
            manual
            manualBox={box}
            onManualBox={setBox}
            onFrameSize={(w, h) => setFrame({ w, h })}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'flex-start' }}>
          <Box sx={{ width: 180 }}>
            <InputField
              label="Object width (cm)"
              type="number"
              value={widthCm}
              onChange={(e) => setWidthCm(e.target.value)}
              fullWidth
            />
          </Box>
          <Box sx={{ width: 180 }}>
            <InputField
              label="Object height (cm)"
              type="number"
              value={heightCm}
              onChange={(e) => setHeightCm(e.target.value)}
              fullWidth
            />
          </Box>
        </Box>
        {existing && (
          <Typography fontSize={13} color="textSecondary" sx={{ mt: 1.5 }}>
            {`Currently calibrated with a ${existing.refWidthCm} × ${existing.refHeightCm} cm reference.`}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button color="primary" onClick={handleCalibrate} variant="contained">
          Calibrate
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CameraCalibrationDialog.propTypes = {
  // @ts-ignore
  camera: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
